import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  Monitor, Maximize2, Minimize2, Plus, Pencil, Star, StarOff,
  LayoutGrid, Save, X, Trash2, ChevronDown, Activity, Bug,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import StreamTile from '../components/multi-site/StreamTile';
import CameraPickerModal from '../components/multi-site/CameraPickerModal';
import CameraFormModal from '../components/modals/CameraFormModal';
import {
  ToolbarIdentity,
  Tooltip,
  usePeerLatency,
  useWallMerge,
  WallGridResizer, useWallTrackSizes, trackTemplate,
  WallSelectionOverlay,
  WallMergeBar,
  WallSplitButton,
  WallEmptyWindow,
  WallEditGuide,
  WallGridMenu,
  normalizeWallMerges,
  flowWallCameras,
  wallGridArea,
  type WallMerge,
  type WallPreset,
} from '@trinetra/shared';
import { CameraState } from '../services/node-connection-manager';
import api, { getNodeCameras } from '../services/api.service';
import {
  isDebugMode, setDebugMode, subscribeDebugMode, subscribeStats, getCameraStats,
  attachCloudMonitorFor, stopCloudMonitorFor, attachedMonitorKeys,
} from '../services/cloud-stats-collector';
import { useAuth } from '../contexts/AuthContext';
import { canUseDebugTools } from '../utils/permissions';
import { useNode } from '../contexts/NodeContext';
import { useNodeStream } from '../contexts/NodeStreamContext';

// ─── Types ─────────────────────────────────────────────────────
export type GridSize = '2x2' | '2x3' | '3x3' | '3x4' | '4x4' | '4x6' | '4x8' | '6x8' | '8x8'

// 4 | 6 | 9 | 12 | 16 | 24 | 32 | 48 | 64
interface GridCamera {
  nodeId: string;
  nodeName: string;
  cameraId: string;
  cameraName: string;
  gridPosition: number;
}

interface SavedView {
  id: string;
  name: string;
  cameras: GridCamera[];
  grid_size: string;
  /** Merged windows (cloud migration 020). Absent on a backend that predates it → a plain grid. */
  merges?: WallMerge[];
  is_default: boolean;
  updated_at: string;
}

interface NodeWithCameras {
  nodeId: string;
  nodeName: string;
  status: string;
  cameras: { cameraId: string; cameraName: string; groupIds?: string[] }[];
  groups?: { id: string; name: string; parent_id: string | null }[];
}

type Mode = 'normal' | 'create' | 'edit';



/** Namespaces this page's stats collectors so they can't be confused with another
 *  page's (the Cameras page attaches its own, unkeyed, for the node in context). */
const MONITOR_KEY_PREFIX = 'multi-site:';

const gridTotal: Record<GridSize, number> ={ '2x2': 4, '2x3': 6, '3x3': 9, '3x4': 12, '4x4': 16, '4x6': 24, '4x8': 32, '6x8': 48, '8x8': 64 };
const gridColNums: Record<GridSize, number> = { '2x2': 2, '2x3': 3, '3x3': 3, '3x4': 4, '4x4': 4, '4x6': 6, '4x8': 8, '6x8': 8, '8x8': 8 };
const GRID_SIZES: GridSize[] = ['2x2', '2x3', '3x3', '3x4', '4x4', '4x6', '4x8', '6x8', '8x8'];

// ─── Component ──────────────────────────────────────────────────
/** Which saved layout this browser last had on the wall, so a reload reopens it. */
const LAST_VIEW_KEY = 'trinetra.allSitesWall.lastView';
function readLastView(): string | null {
  try { return localStorage.getItem(LAST_VIEW_KEY); } catch { return null; }
}
function writeLastView(id: string | undefined) {
  try { if (id) localStorage.setItem(LAST_VIEW_KEY, id); } catch { /* private window */ }
}

/**
 * True when we sent combined windows but the stored row came back without a `merges` field —
 * a cloud backend older than migration 020, which ignores the field without an error.
 */
function mergesDropped(stored: { merges?: unknown } | undefined | null, sent: WallMerge[]): boolean {
  return sent.length > 0 && !!stored && !Array.isArray(stored.merges);
}
const MERGES_DROPPED =
  'Layout saved, but the server did not keep the combined windows — the cloud backend needs updating (migration 020).';

export default function MultiSiteView() {
  // ─── State ──────────────────────────────────────────────────
  const [mode, setMode] = useState<Mode>('normal');
  const [nodes, setNodes] = useState<NodeWithCameras[]>([]);
  const [loadingNodes, setLoadingNodes] = useState(true);
  const [loadingLayout, setLoadingLayout] = useState(true);
  const [savedViews, setSavedViews] = useState<SavedView[]>([]);
  const [currentView, setCurrentView] = useState<SavedView | null>(null);

  // Grid state — sparse array indexed by position (null = empty slot)
  const [gridCameras, setGridCameras] = useState<(GridCamera | null)[]>([]);
  const [gridSize, setGridSize] = useState<GridSize>('2x2');
  // Cells combined into one bigger window — the shared wallLayout, same as each site's own
  // wall. Stored with the layout in the cloud's multi_site_views.merges. Empty = a plain grid.
  const [merges, setMerges] = useState<WallMerge[]>([]);
  const [layoutName, setLayoutName] = useState('');

  // Streams come from the shared, session-level connection provider — this page
  // is a *subscriber*, it no longer owns a NodeConnectionManager. Navigating away
  // and back keeps the WS+PC warm, so re-opening is instant.
  const { streams, states: cameraStates, subscribe, unsubscribe, manager } = useNodeStream();
  // Cameras this page currently has subscribed, keyed "nodeId:cameraId" → for diffing.
  const subscribedRef = useRef<Map<string, GridCamera>>(new Map());

  // Fullscreen
  const [fullscreenTileKey, setFullscreenTileKey] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  // Per-tile wrapper elements, so single-camera fullscreen can requestFullscreen() the exact tile.
  const tileWrapperRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Camera picker
  const [pickerSlot, setPickerSlot] = useState<number | null>(null);

  // Camera edit — a tile edits its camera IN PLACE, in the same modal the camera pages use,
  // instead of navigating to that camera's node-scoped detail page: a one-field change
  // shouldn't cost the operator the wall (and every tile's live track). The all-cameras
  // catalogue behind this grid carries only id + name, so the full row is fetched from the
  // camera's OWN node on click — which is also why the node selection no longer has to move.
  const [editCamera, setEditCamera] = useState<{ camera: any; nodeId: string } | null>(null);
  const [editCameraLoading, setEditCameraLoading] = useState(false);

  // Layout dropdown
  const [layoutDropdownOpen, setLayoutDropdownOpen] = useState(false);

  // Grid-size dropdown (create/edit mode)
  const [gridSizeDropdownOpen, setGridSizeDropdownOpen] = useState(false);

  // Remembers the view that was loaded before entering create mode, so a
  // cancelled "New Layout" restores it instead of leaving an empty grid.
  const preCreateViewRef = useRef<SavedView | null>(null);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);

  // Debug overlay — `debugEnabled` mirrors the app-wide flag; the tick just forces a
  // re-render when the collectors publish a new sample.
  const [debugEnabled, setDebugEnabled] = useState(isDebugMode());
  const [, setStatsTick] = useState(0);

  // Ping overlay
  const [showPing, setShowPing] = useState(false);

  // ─── Mobile portrait detection ─────────────────────────────
  const [isMobilePortrait, setIsMobilePortrait] = useState(false);
  useEffect(() => {
    const check = () => {
      // Consider mobile if width < 768 and in portrait
      setIsMobilePortrait(window.innerWidth < 768 && window.innerHeight > window.innerWidth);
    };
    check();
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);

  // ─── Helpers ────────────────────────────────────────────────
  const showToast = useCallback((message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const usedCameraKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const cam of gridCameras) {
      if (cam) keys.add(`${cam.nodeId}:${cam.cameraId}`);
    }
    return keys;
  }, [gridCameras]);

  // RBAC pre-check: `/multi-site/all-cameras` is RBAC-filtered per the acting cloud
  // user, so `nodes` holds exactly the cameras this user may access, per node. A
  // layout (authored by anyone) can reference cameras this viewer can't see.
  // accessibleByNode[nodeId] = set of allowed camera ids, but ONLY for nodes we
  // actually got a list from (online). We never mark a camera denied for a node
  // that isn't in the catalog (offline/unknown) — that's a reachability issue, and
  // the SFU's own `denied` signal (node-connection-manager) is the authoritative
  // backstop once the node is online.
  const accessibleByNode = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const n of nodes) m.set(n.nodeId, new Set((n.cameras || []).map((c) => c.cameraId)));
    return m;
  }, [nodes]);

  const isAccessDenied = useCallback(
    (nodeId: string, cameraId: string) => {
      const allowed = accessibleByNode.get(nodeId);
      return !!allowed && !allowed.has(cameraId); // node listed, camera absent ⇒ no access
    },
    [accessibleByNode],
  );

  // Ensure grid array has correct length for gridSize
  const ensureGridLength = useCallback(
    (cams: (GridCamera | null)[], size: GridSize): (GridCamera | null)[] => {
      const total = gridTotal[size];
      const result = [...cams];
      if (result.length > total) result.length = total;
      while (result.length < total) result.push(null);
      return result;
    },
    []
  );

  const { hasPermission, user } = useAuth();
  const canEdit = hasPermission('multi-site.edit');

  // ─── Per-camera actions (edit / config / playback) ─────────
  // Every destination below is node-scoped: the camera pages are addressed by
  // :nodeId, and /recordings resolves ?camera= against the node in context, not
  // the URL. This grid spans many nodes, so a tile's camera usually does NOT
  // belong to the currently selected one — switch the main node selection first,
  // then navigate. Same order as the dashboard's node rows (NodeSummaryRow.go).
  const navigate = useNavigate();
  const { selectedNode, selectNode } = useNode();
  const openOnNode = useCallback(
    (nodeId: string, path: string) => {
      if (nodeId && selectedNode?.nodeId !== nodeId) selectNode(nodeId);
      navigate(path);
    },
    [selectedNode, selectNode, navigate],
  );

  const canEditCamera = hasPermission('cameras.edit');
  const canConfigCamera = hasPermission('cameras.config');
  const canViewRecordings = hasPermission('recordings.view');
  const canDebug = canUseDebugTools(user);
  // The flag is app-wide and persisted, so it can outlive the role that set it
  // (role change, shared browser) — the check above is what actually decides.
  const debugActive = canDebug && debugEnabled;

  // ─── Reconcile subscriptions to the current grid (layout-diff) ───
  // A single effect keeps the shared manager in sync with whatever cameras are
  // in the grid: on any layout / grid change we unsubscribe only cameras that
  // left and subscribe only ones that arrived. Cameras shared between the old
  // and new layout are untouched — so switching to a layout that reuses the same
  // node/cameras does NOT rebuild the WebRTC connection.
  useEffect(() => {
    const desired = new Map<string, GridCamera>();
    for (const cam of gridCameras) {
      // Skip cameras the current user has no access to — don't even open a track.
      // The tile renders an "access denied" overlay (see the grid render below).
      if (cam && !isAccessDenied(cam.nodeId, cam.cameraId)) {
        desired.set(`${cam.nodeId}:${cam.cameraId}`, cam);
      }
    }
    const prev = subscribedRef.current;
    for (const [key, cam] of prev) {
      if (!desired.has(key)) unsubscribe(cam.nodeId, cam.cameraId);
    }
    for (const [key, cam] of desired) {
      if (!prev.has(key)) subscribe(cam.nodeId, cam.nodeName, cam.cameraId, cam.cameraName);
    }
    subscribedRef.current = desired;
  }, [gridCameras, subscribe, unsubscribe, isAccessDenied]);

  // On unmount (navigate away), release all of this page's subscriptions. The
  // selected node stays warm (held by the provider); secondary nodes linger then
  // drop. We do NOT tear down the shared connection.
  useEffect(() => {
    return () => {
      for (const [, cam] of subscribedRef.current) unsubscribe(cam.nodeId, cam.cameraId);
      subscribedRef.current = new Map();
    };
  }, [unsubscribe]);

  // ─── Fetch nodes + cameras ────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        setLoadingNodes(true);
        const resp = await api.get('/multi-site/all-cameras');
        setNodes(resp.data.data?.nodes || []);
      } catch {
        showToast('Failed to load nodes', 'error');
      } finally {
        setLoadingNodes(false);
      }
    })();
  }, [showToast]);

  // ─── Fetch saved views + load default ─────────────────────
  useEffect(() => {
    (async () => {
      try {
        setLoadingLayout(true);
        const [viewsResp, defaultResp] = await Promise.all([
          api.get('/multi-site/views'),
          api.get('/multi-site/views/default'),
        ]);
        const views: SavedView[] = viewsResp.data.data?.views || [];
        setSavedViews(views);

        // Reopen the layout this browser last had on the wall — a reload must not swap it for
        // the default (which read as "my combined windows were undone"). Default otherwise.
        const lastId = readLastView();
        const last = lastId ? views.find((v) => v.id === lastId) : undefined;
        const defaultView: SavedView | null = defaultResp.data.data?.view;
        if (last) loadViewIntoGrid(last);
        else if (defaultView) loadViewIntoGrid(defaultView);
      } catch {
        // ignore
      } finally {
        setLoadingLayout(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Browser fullscreen (with iOS Safari webkit prefix support) ───
  const getFullscreenElement = useCallback(() => {
    return document.fullscreenElement || (document as any).webkitFullscreenElement || null;
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      const fsEl = getFullscreenElement() as HTMLElement | null;
      setIsFullscreen(!!fsEl);
      // Single-tile fullscreen tracks the actual fullscreen element (tagged data-tilekey), so
      // native exits (Esc, gestures) stay in sync. Grid-maximize has no data-tilekey → null.
      setFullscreenTileKey(fsEl?.getAttribute?.('data-tilekey') || null);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, [getFullscreenElement]);

  const toggleBrowserFullscreen = useCallback(() => {
    const el = gridContainerRef.current;
    if (!el) return;
    if (!getFullscreenElement()) {
      const reqFs = el.requestFullscreen?.bind(el) || (el as any).webkitRequestFullscreen?.bind(el);
      if (reqFs) {
        reqFs().then(() => {
          try { (screen.orientation as any).lock?.('landscape').catch(() => { }); } catch { }
        }).catch(() => { });
      }
    } else {
      const exitFs = document.exitFullscreen?.bind(document) || (document as any).webkitExitFullscreen?.bind(document);
      if (exitFs) {
        exitFs().then(() => {
          try { (screen.orientation as any).unlock?.(); } catch { }
        }).catch(() => { });
      }
    }
  }, [getFullscreenElement]);

  // ─── Load a view into the grid ────────────────────────────
  const loadViewIntoGrid = useCallback(
    (view: SavedView) => {
      // Only build the grid state — the reconcile effect diffs subscriptions,
      // so a layout that reuses cameras from the previous one keeps them playing.
      const size = (view.grid_size as GridSize) || '2x2';
      const total = gridTotal[size];
      const grid: (GridCamera | null)[] = Array(total).fill(null);

      const cams: GridCamera[] = view.cameras || [];
      for (const cam of cams) {
        if (cam.gridPosition >= 0 && cam.gridPosition < total) {
          grid[cam.gridPosition] = cam;
        }
      }

      setGridCameras(grid);
      setGridSize(size);
      setMerges(normalizeWallMerges(view.merges, size));
      setCurrentView(view);
      writeLastView(view.id);
      setFullscreenTileKey(null);
      setMode('normal');
    },
    []
  );

  // ─── Camera added from picker ─────────────────────────────
  const handleCameraSelected = useCallback(
    (nodeId: string, nodeName: string, cameraId: string, cameraName: string) => {
      if (pickerSlot === null) return;
      const cam: GridCamera = { nodeId, nodeName, cameraId, cameraName, gridPosition: pickerSlot };
      // Just update the grid — the reconcile effect subscribes the new camera and
      // unsubscribes any that was previously in this slot.
      setGridCameras((prev) => {
        const next = [...prev];
        next[pickerSlot] = cam;
        return next;
      });
      setPickerSlot(null);
    },
    [pickerSlot]
  );

  // ─── Remove camera from slot ──────────────────────────────
  const handleRemoveFromSlot = useCallback((position: number) => {
    setGridCameras((prev) => {
      const next = [...prev];
      next[position] = null;
      return next;
    });
  }, []);

  // ─── Grid size change ─────────────────────────────────────
  const handleGridSizeChange = useCallback(
    (newSize: GridSize) => {
      setGridSize(newSize);
      // Merges are rectangles on ONE grid shape; on another they would cover different cells,
      // so a new size starts plain. Said out loud, because it undoes work.
      if (merges.length) showToast('Combined windows were reset for the new grid size', 'warning');
      setMerges([]);
      setGridCameras((prev) => ensureGridLength(prev, newSize));
    },
    [ensureGridLength, merges.length, showToast]
  );

  /** A ready-made shape: size + merges at once, cameras flowed into its windows in order. */
  const handlePreset = useCallback((preset: WallPreset) => {
    const size = preset.gridSize as GridSize;
    setGridSize(size);
    setMerges(preset.merges);
    setGridCameras((prev) => flowWallCameras(prev.filter(Boolean) as GridCamera[], size, preset.merges));
  }, []);

  // ─── Save / Create layout ────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!layoutName.trim()) {
      showToast('Enter a layout name', 'warning');
      return;
    }

    const cameras = gridCameras
      .map((cam, i) => (cam ? { ...cam, gridPosition: i } : null))
      .filter(Boolean);

    try {
      if (mode === 'create') {
        const resp = await api.post('/multi-site/views', {
          name: layoutName.trim(),
          cameras,
          gridSize,
          merges,
        });
        const view = resp.data.data?.view;
        setSavedViews((prev) => [view, ...prev]);
        setCurrentView(view);
        writeLastView(view?.id);
        if (mergesDropped(view, merges)) {
          setMerges([]);
          showToast(MERGES_DROPPED, 'warning');
        } else {
          showToast(`Layout "${layoutName}" saved`, 'success');
        }
      } else if (mode === 'edit' && currentView) {
        const resp = await api.put(`/multi-site/views/${currentView.id}`, {
          name: layoutName.trim(),
          cameras,
          gridSize,
          merges,
        });
        // The server's row, not our own copy: it is what a reload will show. Building `updated`
        // locally is how a backend that silently ignored `merges` looked like it had saved them.
        const stored: SavedView | undefined = resp.data.data?.view;
        const dropped = mergesDropped(stored, merges);
        const updated = {
          ...currentView,
          ...(stored || {}),
          name: layoutName.trim(),
          cameras: (stored?.cameras || cameras) as GridCamera[],
          grid_size: gridSize,
          merges: dropped ? [] : (stored?.merges ?? merges),
          updated_at: stored?.updated_at || new Date().toISOString(),
        };
        setSavedViews((prev) => prev.map((v) => (v.id === currentView.id ? updated : v)));
        setCurrentView(updated);
        if (dropped) {
          setMerges([]);
          showToast(MERGES_DROPPED, 'warning');
        } else {
          showToast('Layout updated', 'success');
        }
      }
      setMode('normal');
    } catch {
      showToast('Failed to save layout', 'error');
    }
  }, [layoutName, gridCameras, gridSize, merges, mode, currentView, showToast]);

  // ─── Delete layout ────────────────────────────────────────
  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await api.delete(`/multi-site/views/${id}`);
        setSavedViews((prev) => prev.filter((v) => v.id !== id));
        if (currentView?.id === id) setCurrentView(null);
        showToast('Layout deleted', 'success');
      } catch {
        showToast('Failed to delete layout', 'error');
      }
    },
    [currentView, showToast]
  );

  // ─── Set default ──────────────────────────────────────────
  const handleToggleDefault = useCallback(
    async (view: SavedView) => {
      try {
        if (view.is_default) {
          await api.delete(`/multi-site/views/${view.id}/set-default`);
          setSavedViews((prev) => prev.map((v) => (v.id === view.id ? { ...v, is_default: false } : v)));
          if (currentView?.id === view.id) setCurrentView((p) => p ? { ...p, is_default: false } : p);
          showToast('Default cleared', 'success');
        } else {
          const resp = await api.put(`/multi-site/views/${view.id}/set-default`);
          setSavedViews((prev) =>
            prev.map((v) => ({ ...v, is_default: v.id === view.id }))
          );
          if (currentView?.id === view.id) setCurrentView((p) => p ? { ...p, is_default: true } : p);
          else setCurrentView(resp.data.data?.view || { ...view, is_default: true });
          showToast('Set as default layout', 'success');
        }
      } catch {
        showToast('Failed to update default', 'error');
      }
    },
    [currentView, showToast]
  );

  // ─── Tile fullscreen toggle ───────────────────────────────
  const handleTileFullscreen = useCallback(
    (key: string) => {
      const [nodeId, cameraId] = key.split(':');
      // Use the browser Fullscreen API on the tile's own element (not a CSS overlay). This keeps
      // the same <video> node mounted — the WebRTC stream keeps playing, no black screen — and is
      // immune to ancestor CSS (transform/overflow) that can trap a position:fixed overlay.
      // fullscreenTileKey is updated by the fullscreenchange handler, not here.
      if (fullscreenTileKey === key) {
        const exitFs = document.exitFullscreen?.bind(document) || (document as any).webkitExitFullscreen?.bind(document);
        exitFs?.().catch(() => {});
        manager()?.changeQuality(nodeId, cameraId, 'low');
      } else {
        const el = tileWrapperRefs.current.get(key);
        const reqFs = el && (el.requestFullscreen?.bind(el) || (el as any).webkitRequestFullscreen?.bind(el));
        if (reqFs) {
          reqFs()
            .then(() => { try { (screen.orientation as any).lock?.('landscape').catch(() => {}); } catch {} })
            .catch(() => {});
        }
        manager()?.changeQuality(nodeId, cameraId, 'high');
      }
    },
    [fullscreenTileKey]
  );

  // ─── Editing a tile's CAMERA (not the layout) ──────────────
  /**
   * Open the camera edit modal for a tile.
   *
   * Leaves fullscreen first: the modal renders outside the element handed to the Fullscreen
   * API, so on a maximized grid — or an expanded tile — it would otherwise open on a surface
   * the browser isn't showing.
   */
  const handleEditCamera = useCallback(async (nodeId: string, cameraId: string) => {
    if (getFullscreenElement()) {
      const exitFs = document.exitFullscreen?.bind(document) || (document as any).webkitExitFullscreen?.bind(document);
      exitFs?.().catch(() => {});
    }
    setEditCameraLoading(true);
    try {
      const data: any = await getNodeCameras(nodeId);
      const list = Array.isArray(data) ? data : (data?.data || data?.cameras || []);
      const cam = list.find((c: any) => (c.id || c.camera_id) === cameraId);
      if (!cam) throw new Error('camera not found');
      setEditCamera({ camera: cam, nodeId });
    } catch {
      showToast('Failed to load camera details', 'error');
    } finally {
      setEditCameraLoading(false);
    }
  }, [getFullscreenElement, showToast]);

  /**
   * After a save. A tile's label comes from the LAYOUT's stored slot, not from the camera
   * catalogue, so a rename is patched in place — on the wall, in the loaded layout and in the
   * saved-layout menu — rather than reloading the layout, which would re-signal every tile.
   */
  const handleCameraSaved = useCallback(async () => {
    if (!editCamera) return;
    const { nodeId } = editCamera;
    const id = editCamera.camera?.id || editCamera.camera?.camera_id;
    if (!id) return;
    try {
      const data: any = await getNodeCameras(nodeId);
      const list = Array.isArray(data) ? data : (data?.data || data?.cameras || []);
      const name = list.find((c: any) => (c.id || c.camera_id) === id)?.name;
      if (!name) return;
      // Matched on node AND camera: camera ids are only unique within a node.
      const rename = (arr: any[]) =>
        (arr || []).map((c: any) =>
          c && c.cameraId === id && c.nodeId === nodeId ? { ...c, cameraName: name } : c);
      setGridCameras((prev) => rename(prev));
      setCurrentView((prev) => (prev ? { ...prev, cameras: rename(prev.cameras) } : prev));
      setSavedViews((prev) => prev.map((v) => ({ ...v, cameras: rename(v.cameras) })));
    } catch {
      /* The name refresh is cosmetic — the save itself already succeeded. */
    }
  }, [editCamera]);

  // ─── Retry ────────────────────────────────────────────────
  const handleRetry = useCallback((nodeId: string, cameraId: string) => {
    manager()?.retryCamera(nodeId, cameraId);
  }, []);

  // ─── Enter create mode ────────────────────────────────────
  const enterCreateMode = useCallback(() => {
    // Remember what was loaded so Cancel can restore it (instead of an empty grid).
    preCreateViewRef.current = currentView;
    // Clearing the grid triggers the reconcile effect to unsubscribe everything.
    setGridCameras(Array(gridTotal['2x2']).fill(null));
    setGridSize('2x2');
    setMerges([]);
    setLayoutName('');
    setCurrentView(null);
    setFullscreenTileKey(null);
    setMode('create');
  }, [currentView]);

  // ─── Edit a specific view (from the layout dropdown) ──────
  const handleEditView = useCallback(
    (view: SavedView) => {
      loadViewIntoGrid(view);   // sets grid/size/currentView and mode → 'normal'
      setLayoutName(view.name);
      setLayoutDropdownOpen(false);
      setMode('edit');
    },
    [loadViewIntoGrid]
  );

  // ─── Cancel edit/create ───────────────────────────────────
  const cancelMode = useCallback(() => {
    if (mode === 'create') {
      // Restore the layout that was active before "New Layout" was clicked; if
      // there was none, clear the grid (reconcile effect unsubscribes everything).
      const prev = preCreateViewRef.current;
      if (prev) {
        loadViewIntoGrid(prev); // also resets mode → 'normal'
        return;
      }
      setMerges([]);
      setGridCameras([]);
    } else if (mode === 'edit' && currentView) {
      // Discard unsaved grid edits by reloading the view as it was saved.
      loadViewIntoGrid(currentView); // also resets mode → 'normal'
      return;
    }
    setMode('normal');
  }, [mode, currentView, loadViewIntoGrid]);

  // ─── Escape key ───────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (fullscreenTileKey) setFullscreenTileKey(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullscreenTileKey]);

  // ─── Derived ──────────────────────────────────────────────
  const isEditing = mode === 'create' || mode === 'edit';
  const cols = gridColNums[gridSize];
  const total = gridTotal[gridSize];
  const activeCameras = gridCameras.filter(Boolean) as GridCamera[];
  const hasAnyCameras = activeCameras.length > 0;

  // Drag-to-combine — the shared merge editor both per-site walls use. A plain click on an
  // EMPTY window still opens the picker, exactly as the bare slot's onClick did; a click on a
  // camera tile does nothing, as before.
  const wall = useWallMerge<GridCamera>({
    gridSize,
    merges,
    setMerges,
    gridCameras,
    setGridCameras,
    isEditing,
    onWindowClick: useCallback((w: { position: number }) => {
      if (!gridCameras[w.position]) setPickerSlot(w.position);
    }, [gridCameras]),
  });
  // Column widths / row heights, dragged on the lines between windows — per saved layout, in
  // this browser only (as on the per-site walls; the saved layout has no field for them). Own
  // key prefix: these layout ids are cloud ids, not a node's. A layout being created has no id
  // yet, so its sizes last until it is saved.
  const rows = Math.ceil(total / cols);
  const tracks = useWallTrackSizes(currentView ? `trinetra.allSitesWall.sizes.${currentView.id}` : null, rows, cols);
  const windowCount = wall.windows.length;

  // ─── Latency overlay ───────────────────────────────────────
  // Read off the existing peer connections (see usePeerLatency) — no extra traffic, and the same
  // hook both Cameras pages use, so a wall of tiles and a page of cards report identically.
  // One connection per node here, so the map is keyed by nodeId.
  const pingData = usePeerLatency(
    useCallback(() => {
      const mgr = manager();
      if (!mgr) return [];
      return mgr.getConnectedNodeIds().map((nodeId: string) => ({ key: nodeId, pc: mgr.getNodePeerConnection(nodeId) }));
    }, [manager]),
    showPing,
  );

  // ─── Debug mode (browser-side WebRTC stats) ────────────────
  // Shares the app-wide debug flag with the Cameras page, so turning it on in one
  // place turns it on in the other — it's one dev tool, not two.
  useEffect(() => {
    const unsubDebug = subscribeDebugMode(setDebugEnabled);
    const unsubStats = subscribeStats(() => setStatsTick((t) => t + 1));
    return () => { unsubDebug(); unsubStats(); };
  }, []);

  // Stats live in the peer connection that carries the track, and this grid can be
  // fed by several nodes at once — so attach a collector per connected node rather
  // than the single one the Cameras page needs. Nodes join and drop while the page
  // is open (the provider lets secondary ones linger, then closes them), so the set
  // is reconciled on a timer instead of being wired up once.
  useEffect(() => {
    if (!debugActive) return;
    const dropOurs = () => {
      for (const key of attachedMonitorKeys()) {
        if (key.startsWith(MONITOR_KEY_PREFIX)) stopCloudMonitorFor(key);
      }
    };

    let stopped = false;
    const reconcile = () => {
      if (stopped) return;
      const mgr = manager();
      const live = new Set(mgr?.getConnectedNodeIds() || []);
      for (const nodeId of live) {
        const pc = mgr?.getNodePeerConnection(nodeId);
        if (pc) attachCloudMonitorFor(MONITOR_KEY_PREFIX + nodeId, pc);
      }
      for (const key of attachedMonitorKeys()) {
        if (key.startsWith(MONITOR_KEY_PREFIX) && !live.has(key.slice(MONITOR_KEY_PREFIX.length))) {
          stopCloudMonitorFor(key);
        }
      }
    };

    reconcile();
    const interval = setInterval(reconcile, 3000);
    return () => { stopped = true; clearInterval(interval); dropOurs(); };
  }, [debugActive]);

  // Single-tile fullscreen is rendered in-place in the grid below (the existing tile is
  // promoted to a fixed overlay via its `isFullscreen` prop). Rendering it here as a
  // separate subtree would unmount/remount the tile's <video>, losing the WebRTC stream
  // and leaving a black screen — so we intentionally do NOT early-return for it.

  // ─── Loading state ────────────────────────────────────────
  if (loadingNodes || loadingLayout) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[60vh] gap-4">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-[3px] border-muted border-t-brand" />
          <Monitor className="absolute inset-0 m-auto h-5 w-5 text-brand" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium text-foreground">Loading Live Wall</p>
          <p className="text-xs text-muted-foreground mt-1">
            {loadingNodes ? 'Fetching nodes & cameras…' : 'Loading default layout…'}
          </p>
        </div>
      </div>
    );
  }

  // ─── RENDER ───────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full min-h-0">
      {/* ─── Toolbar ─────────────────────────────────────────── */}
      {/* The same three zones and the same card chrome as the alert board's bar, so the two live
          surfaces read as one product: left = what am I looking at, centre = which layout (or,
          mid-edit, what shape it is), right = the actions. Both side zones are `flex-1`, which is
          what keeps the centre control optically centred between two unequal zones. */}
      <div className="mb-2 flex flex-shrink-0 items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-1.5 sm:px-3 sm:py-2">
        {/* ─── Left zone — which wall, and what is on it ─── */}
        {isEditing ? (
          /* Mid-edit the layout's NAME is being typed, so the field takes the place of the
             headline — it is the same thing in the same spot, editable. */
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-2.5">
            <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-brand/10 text-brand sm:h-9 sm:w-9">
              <Monitor className="h-4 w-4 sm:h-5 sm:w-5" />
            </span>
            <div className="flex min-w-0 flex-col justify-center leading-tight">
              <input
                type="text"
                value={layoutName}
                onChange={(e) => setLayoutName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                placeholder="Layout name…"
                autoFocus
                className="w-full max-w-xs rounded border border-border bg-background px-2 py-0.5 text-[15px] font-bold text-foreground placeholder:font-normal placeholder:text-muted-foreground focus:border-brand focus:outline-none sm:text-base"
              />
              <span className="mt-0.5 hidden max-w-full truncate whitespace-nowrap text-[11px] text-muted-foreground sm:block">
                <span className="font-semibold uppercase tracking-wide text-brand">{mode === 'create' ? 'New' : 'Editing'}</span>
                {' · all sites · click a window to add a camera · drag across windows to combine them'}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex min-w-0 flex-1 items-center">
            <ToolbarIdentity
              icon={Monitor}
              // "Live Wall: Lobby & Gates" — the kind of surface, then the layout on it. The
              // layout name alone was a proper noun in a corner with nothing to say what it named.
              kind="Live Wall:"
              title={currentView?.name || 'no layout selected'}
              titleText={currentView ? `Live Wall: ${currentView.name}` : undefined}
              detail={
                currentView
                  ? `All sites · ${activeCameras.length} camera${activeCameras.length === 1 ? '' : 's'} · ${gridSize} grid${merges.length ? ` · ${windowCount} windows` : ''}`
                  : 'All sites · pick a saved layout, or create one'
              }
            />
          </div>
        )}

        {/* ─── Centre zone — which layout you're watching; mid-edit, what shape it is ─── */}
        <div className="flex flex-none items-center justify-center">
          {isEditing ? (
            /* Grid size — the shape of the wall, chosen while building it. */
            <div className="relative">
              <button
                onClick={() => setGridSizeDropdownOpen((o) => !o)}
                className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium bg-muted text-foreground hover:bg-accent transition-colors"
              >
                <LayoutGrid className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span>{gridSize}</span>
                <ChevronDown className={`h-3 w-3 sm:h-3.5 sm:w-3.5 transition-transform ${gridSizeDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {gridSizeDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setGridSizeDropdownOpen(false)} />
                  <div className="absolute top-full right-0 mt-1 w-64 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden">
                    <WallGridMenu
                      gridSize={gridSize}
                      merges={merges}
                      sizes={GRID_SIZES.map((size) => ({ size, total: gridTotal[size] }))}
                      onSize={(size) => { handleGridSizeChange(size as GridSize); setGridSizeDropdownOpen(false); }}
                      onPreset={(preset) => { handlePreset(preset); setGridSizeDropdownOpen(false); }}
                    />
                  </div>
                </>
              )}
            </div>
          ) : (
            /* Which saved layout is on the wall — the Live Wall's answer to the alert board's
               view toggle: the one control that says what you are looking at. */
            <div className="relative">
              <button
                onClick={() => setLayoutDropdownOpen(!layoutDropdownOpen)}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium bg-muted text-foreground hover:bg-accent transition-colors"
              >
                <LayoutGrid className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                {/* Names the MENU, not the layout: the layout on screen is named in the bar's
                    left corner, so repeating it on the trigger would say it twice. */}
                <span className="hidden sm:inline">Layouts</span>
                <ChevronDown className={`h-3 w-3 sm:h-3.5 sm:w-3.5 transition-transform ${layoutDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {layoutDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setLayoutDropdownOpen(false)} />
                  <div className="absolute top-full right-0 mt-1 w-72 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden">
                    {savedViews.length === 0 ? (
                      <p className="px-3 py-4 text-sm text-muted-foreground text-center">No saved layouts</p>
                    ) : (
                      <div className="max-h-60 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                        {savedViews.map((view) => (
                          <div
                            key={view.id}
                            className={`flex items-center gap-2 px-3 py-2 hover:bg-accent/50 transition-colors ${currentView?.id === view.id ? 'bg-brand/10' : ''
                              }`}
                          >
                            <button
                              onClick={() => {
                                loadViewIntoGrid(view);
                                setLayoutDropdownOpen(false);
                              }}
                              className="group/name flex-1 text-left min-w-0"
                            >
                              <div className="flex items-center gap-1.5">
                                <p
                                  title={view.name}
                                  className="text-sm font-medium text-foreground truncate transition-colors group-hover/name:text-brand"
                                >
                                  {view.name}
                                </p>
                                {view.is_default && (
                                  <Star className="h-3 w-3 text-amber-500 fill-amber-500 flex-shrink-0" />
                                )}
                              </div>
                              <p className="text-[10px] text-muted-foreground">
                                {(view.cameras || []).length} cameras · {view.grid_size}
                                {view.merges && view.merges.length ? ' · combined windows' : ''}
                              </p>
                            </button>

                            {canEdit && (
                              <Tooltip label="Edit layout" side="top" className="flex-shrink-0">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditView(view);
                                  }}
                                  className="p-1 text-muted-foreground hover:text-brand transition-colors"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                              </Tooltip>
                            )}

                            {canEdit && (
                              <Tooltip label={view.is_default ? 'Remove default' : 'Set as default'} side="top" className="flex-shrink-0">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleDefault(view);
                                  }}
                                  className="p-1 text-muted-foreground hover:text-amber-500 transition-colors"
                                >
                                  {view.is_default ? (
                                    <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                                  ) : (
                                    <StarOff className="h-3.5 w-3.5" />
                                  )}
                                </button>
                              </Tooltip>
                            )}

                            {canEdit && (
                              <Tooltip label="Delete layout" side="top" className="flex-shrink-0">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(view.id);
                                  }}
                                  className="p-1 text-muted-foreground hover:text-red-400 transition-colors"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </Tooltip>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* ─── Right zone — the actions ─── */}
        <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:gap-2">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                className="flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium bg-brand text-white hover:bg-brand/90 transition-colors"
              >
                <Save className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Save</span>
              </button>

              <button
                onClick={cancelMode}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                title="Cancel"
              >
                <X className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              {/* New layout — icon-only; the label lives in the hover tooltip. */}
              {canEdit && (
                <Tooltip label="New layout" side="bottom">
                  <button
                    onClick={enterCreateMode}
                    aria-label="New layout"
                    className="p-1.5 rounded-lg bg-brand text-white shadow-sm ring-1 ring-inset ring-white/10 hover:bg-brand/90 hover:shadow active:scale-[0.95] transition-all"
                  >
                    <Plus className="h-4 w-4" strokeWidth={2.5} />
                  </button>
                </Tooltip>
              )}

              {/* Only once a line between windows has been dragged — the way back to even. */}
              {tracks.uneven && (
                <Tooltip label="Even out window sizes (or double-click a line between windows)" side="bottom">
                  <button
                    onClick={tracks.reset}
                    aria-label="Even out window sizes"
                    className="flex items-center gap-1 p-1.5 rounded-lg text-xs sm:text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  >
                    <LayoutGrid className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="hidden lg:inline">Even sizes</span>
                  </button>
                </Tooltip>
              )}

              {/* Debug overlay toggle — same app-wide flag as the Cameras page */}
              {canDebug && (
                <Tooltip label={debugEnabled ? 'Hide WebRTC debug stats' : 'Show WebRTC debug stats'} side="bottom">
                  <button
                    onClick={() => setDebugMode(!debugEnabled)}
                    aria-label="Toggle debug stats"
                    className={`p-1.5 rounded-lg transition-colors ${debugEnabled
                      ? 'bg-amber-500/15 text-amber-400'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                      }`}
                  >
                    <Bug className="h-4 w-4 sm:h-5 sm:w-5" />
                  </button>
                </Tooltip>
              )}

              {/* Ping / latency overlay toggle — the readout it turns on sits top-right of each
                  tile, clear of the hover actions in the tile's bottom bar. */}
              <Tooltip label={showPing ? 'Hide network latency' : 'Show network latency'} side="bottom">
                <button
                  onClick={() => setShowPing(!showPing)}
                  aria-label="Toggle network latency"
                  className={`p-1.5 rounded-lg transition-colors ${showPing
                    ? 'bg-green-500/15 text-green-400'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                    }`}
                >
                  <Activity className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </Tooltip>

              {/* Browser fullscreen */}
              <Tooltip label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} side="bottom">
                <button
                  onClick={toggleBrowserFullscreen}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                >
                  {isFullscreen ? <Minimize2 className="h-4 w-4 sm:h-5 sm:w-5" /> : <Maximize2 className="h-4 w-4 sm:h-5 sm:w-5" />}
                </button>
              </Tooltip>
            </>
          )}
        </div>
      </div>

      {/* How to shape the wall — only while editing, folded to one line once read. */}
      {isEditing && <WallEditGuide />}

      {/* ─── Grid area ───────────────────────────────────────── */}
      <div
        ref={gridContainerRef}
        className={`flex-1 min-h-0 overflow-hidden relative ${isFullscreen ? 'bg-black' : ''}`}
        style={
          isFullscreen
            ? isMobilePortrait
              ? {
                /* Rotate 90° on portrait mobile: swap width/height */
                width: '100vh',
                height: '100vw',
                transform: 'rotate(90deg)',
                transformOrigin: 'top left',
                position: 'absolute',
                top: 0,
                left: '100vw',
                padding: '2px',
              }
              : { width: '100vw', height: '100vh', padding: '2px' }
            : undefined
        }
      >
        {/* Fullscreen exit overlay */}
        {isFullscreen && (
          <button
            onClick={toggleBrowserFullscreen}
            className="absolute top-3 right-3 z-20 rounded-lg p-2 text-white bg-black/50 hover:bg-black/70 transition-colors"
          >
            <Minimize2 className="h-5 w-5" />
          </button>
        )}

        {!currentView && !hasAnyCameras && !isEditing ? (
          /* Empty state — normal mode, no layout loaded */
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <Monitor className="h-12 w-12 mb-3 opacity-30" />
            <p className="text-sm mb-4">No layout loaded</p>
            {canEdit && (
              <button
                onClick={enterCreateMode}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-brand text-white hover:bg-brand/90 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Create New Layout
              </button>
            )}
          </div>
        ) : (
          /* Grid — one item per WINDOW, not per cell: a merged window is one item spanning its
             rectangle, and the cells it covers are simply not drawn. */
          <div
            className={`relative grid h-full ${isFullscreen ? 'gap-0.5' : 'gap-1.5 sm:gap-2'} ${wall.gridProps.className}`}
            style={{
              gridTemplateColumns: trackTemplate(tracks.colSizes),
              gridTemplateRows: trackTemplate(tracks.rowSizes),
              ...wall.gridProps.style,
            }}
            onPointerDown={wall.gridProps.onPointerDown}
            onPointerMove={wall.gridProps.onPointerMove}
            onPointerUp={wall.gridProps.onPointerUp}
            onPointerCancel={wall.gridProps.onPointerCancel}
          >
            <WallGridResizer
              rows={rows}
              cols={cols}
              windows={wall.windows}
              colSizes={tracks.colSizes}
              rowSizes={tracks.rowSizes}
              onChange={tracks.onChange}
            />
            {wall.windows.map((w) => {
              const i = w.position;
              const cam = gridCameras[i] || null;

              if (!cam) {
                return (
                  <WallEmptyWindow
                    key={`slot-${i}`}
                    window={w}
                    isEditing={isEditing}
                    onSplit={() => wall.split(i)}
                  />
                );
              }

              // Filled slot
              const key = `${cam.nodeId}:${cam.cameraId}`;
              const isTileFullscreen = fullscreenTileKey === key;
              const denied = isAccessDenied(cam.nodeId, cam.cameraId);
              // A camera missing from this user's RBAC-filtered catalog gets no actions:
              // the destinations would only refuse them a step later.
              const showActions = !isEditing && !denied;
              return (
                <div
                  key={key}
                  data-tilekey={key}
                  ref={(el: HTMLDivElement | null) => {
                    const m = tileWrapperRefs.current;
                    if (el) m.set(key, el); else m.delete(key);
                  }}
                  data-wallpos={i}
                  style={wallGridArea(w)}
                  className={`relative overflow-hidden ${isTileFullscreen ? 'bg-black' : ''}`}
                >
                  <StreamTile
                    nodeId={cam.nodeId}
                    nodeName={cam.nodeName}
                    cameraId={cam.cameraId}
                    cameraName={cam.cameraName}
                    stream={denied ? null : (streams.get(key) || null)}
                    state={denied ? 'access_denied' : (cameraStates.get(key) || 'pending')}
                    isFullscreen={isTileFullscreen}
                    fillContainer={true}
                    pingInfo={showPing ? pingData.get(cam.nodeId) || null : null}
                    debugStats={debugActive && !denied ? getCameraStats(cam.cameraId) : null}
                    onDoubleClick={() => handleTileFullscreen(key)}
                    onRetry={() => handleRetry(cam.nodeId, cam.cameraId)}
                    // Actions belong to viewing a layout: while editing one, the tile's own
                    // controls (remove) own that corner and navigating away would discard
                    // unsaved slot changes.
                    onEdit={showActions && canEditCamera
                      ? () => handleEditCamera(cam.nodeId, cam.cameraId)
                      : undefined}
                    onConfig={showActions && canConfigCamera
                      ? () => openOnNode(cam.nodeId, `/nodes/${cam.nodeId}/cameras/${cam.cameraId}/config`)
                      : undefined}
                    onPlayback={showActions && canViewRecordings
                      ? () => openOnNode(cam.nodeId, `/recordings?camera=${cam.cameraId}`)
                      : undefined}
                    onStalled={() => manager()?.handleCameraStall(cam.nodeId, cam.cameraId)}
                    onPlaying={() => manager()?.handleCameraPlaying(cam.nodeId, cam.cameraId)}
                  />
                  {/* Exit button, shown while this tile is expanded fullscreen */}
                  {isTileFullscreen && (
                    <button
                      onClick={() => handleTileFullscreen(key)}
                      className="absolute top-4 right-4 z-[60] p-2 bg-black/60 hover:bg-black/80 text-white rounded-lg transition-colors"
                      title="Exit fullscreen"
                    >
                      <Minimize2 className="h-5 w-5" />
                    </button>
                  )}
                  {/* Remove button in edit mode */}
                  {isEditing && !isTileFullscreen && (
                    <button
                      onClick={() => handleRemoveFromSlot(i)}
                      className="absolute top-1.5 right-1.5 z-10 p-1 bg-black/60 hover:bg-red-500/80 text-white rounded-md transition-colors"
                      title="Remove"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {isEditing && !isTileFullscreen && w.merged && <WallSplitButton onSplit={() => wall.split(i)} />}
                </div>
              );
            })}

            <WallSelectionOverlay selection={wall.selection} dragging={wall.dragging} />
          </div>
        )}

        {/* What the selection will become, and Merge / Cancel. */}
        {isEditing && <WallMergeBar pending={wall.pending} onMerge={wall.commit} onCancel={wall.cancel} />}
      </div>

      {/* ─── Camera Picker Modal ─────────────────────────────── */}
      <CameraPickerModal
        open={pickerSlot !== null}
        onClose={() => setPickerSlot(null)}
        onSelect={handleCameraSelected}
        nodes={nodes}
        loading={loadingNodes}
        usedCameraKeys={usedCameraKeys}
      />

      {/* ─── Camera edit — in place, so the wall keeps streaming ── */}
      <CameraFormModal
        isOpen={!!editCamera}
        onClose={() => setEditCamera(null)}
        onSuccess={handleCameraSaved}
        camera={editCamera?.camera || null}
        nodeId={editCamera?.nodeId || ''}
      />

      {/* Covers the tunnel round trip that fetches the row the form needs, so a click on a
          slow link isn't a dead button. */}
      {editCameraLoading && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="px-4 py-2.5 rounded-lg bg-card text-sm text-muted-foreground shadow-lg flex items-center gap-2">
            <div className="animate-spin rounded-full h-3.5 w-3.5 border-t-2 border-brand" />
            Loading camera…
          </div>
        </div>
      )}

      {/* ─── Toast ───────────────────────────────────────────── */}
      {toast && (
        <div
          className={`fixed bottom-4 right-4 z-[60] px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg transition-all ${toast.type === 'success'
            ? 'bg-emerald-500/90 text-white'
            : toast.type === 'warning'
              ? 'bg-amber-500/90 text-white'
              : 'bg-red-500/90 text-white'
            }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
